import React from 'react';
import { StatusBar } from 'expo-status-bar';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Text, View, ActivityIndicator, StyleSheet } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';

import { AuthProvider, useAuth } from './src/context/AuthContext';
import { AlertProvider } from './src/components/CustomAlert';
import { COLORS } from './src/config';

// Screens
import LoginScreen from './src/screens/LoginScreen';
import RegisterScreen from './src/screens/RegisterScreen';
import DashboardScreen from './src/screens/DashboardScreen';
import HadiahScreen from './src/screens/HadiahScreen';
import RiwayatScreen from './src/screens/RiwayatScreen';
import ProfileScreen from './src/screens/ProfileScreen';
import TopupScreen from './src/screens/TopupScreen';
import VoucherScreen from './src/screens/VoucherScreen';
import BantuanScreen from './src/screens/BantuanScreen';
import LokasiScreen from './src/screens/LokasiScreen';
import KartuMemberScreen from './src/screens/KartuMemberScreen';
import UndianScreen from './src/screens/UndianScreen';
import ForgotPasswordScreen from './src/screens/ForgotPasswordScreen';

const Stack = createNativeStackNavigator();
const Tab = createBottomTabNavigator();

function TabBarIcon({ focused, icon }) {
  return (
    <View style={[styles.tabIcon, focused && styles.tabIconActive]}>
      <Feather name={icon} size={24} color={focused ? COLORS.primary : COLORS.textMuted} />
    </View>
  );
}

// Stack for Home Tab (Dashboard + Voucher + Lokasi)
const HomeStack = createNativeStackNavigator();
function HomeStackScreen() {
  return (
    <HomeStack.Navigator screenOptions={{ headerShown: false }}>
      <HomeStack.Screen name="Dashboard" component={DashboardScreen} />
      <HomeStack.Screen name="Voucher" component={VoucherScreen} />
      <HomeStack.Screen name="Lokasi" component={LokasiScreen} />
    </HomeStack.Navigator>
  );
}

function MainTabs() {
  const insets = useSafeAreaInsets();

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarStyle: {
          backgroundColor: COLORS.bgDarker,
          borderTopColor: 'transparent',
          borderTopWidth: 0,
          height: 56 + insets.bottom,
          paddingBottom: insets.bottom + 6,
          paddingTop: 6,
          paddingHorizontal: 8,
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          elevation: 0,
          shadowOpacity: 0,
        },
        tabBarActiveTintColor: COLORS.primary,
        tabBarInactiveTintColor: COLORS.textMuted,
        tabBarIcon: ({ focused }) => {
          let icon;
          switch (route.name) {
            case 'HomeTab':
              icon = 'home';
              break;
            case 'Hadiah':
              icon = 'gift';
              break;
            case 'Riwayat':
              icon = 'list';
              break;
            case 'Topup':
              icon = 'credit-card';
              break;
            case 'Profile':
              icon = 'user';
              break;
            default:
              icon = 'circle';
          }
          return <TabBarIcon focused={focused} icon={icon} />;
        },
        tabBarLabelStyle: {
          fontSize: 10,
          fontWeight: '500',
          marginTop: -4,
        },
        tabBarItemStyle: {
          paddingHorizontal: 2,
          paddingVertical: 4,
        },
      })}
    >
      <Tab.Screen
        name="HomeTab"
        component={HomeStackScreen}
        options={{ tabBarLabel: 'Home' }}
      />
      <Tab.Screen name="Hadiah" component={HadiahScreen} options={{ tabBarLabel: 'Hadiah' }} />
      <Tab.Screen name="Riwayat" component={RiwayatScreen} options={{ tabBarLabel: 'Riwayat' }} />
      <Tab.Screen name="Topup" component={TopupScreen} options={{ tabBarLabel: 'Topup' }} />
      <Tab.Screen name="Profile" component={ProfileScreen} options={{ tabBarLabel: 'Profil' }} />
    </Tab.Navigator>
  );
}

// Main app with Stack for additional screens
function MainStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="MainTabs" component={MainTabs} />
      <Stack.Screen name="Bantuan" component={BantuanScreen} />
      <Stack.Screen name="Lokasi" component={LokasiScreen} />
      <Stack.Screen name="KartuMember" component={KartuMemberScreen} />
      <Stack.Screen name="Undian" component={UndianScreen} />
    </Stack.Navigator>
  );
}

function AuthStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Login" component={LoginScreen} />
      <Stack.Screen name="Register" component={RegisterScreen} />
      <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} />
    </Stack.Navigator>
  );
}

function AppNavigator() {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  return (
    <NavigationContainer>
      {isLoggedIn ? <MainStack /> : <AuthStack />}
    </NavigationContainer>
  );
}

export default function App() {
  return (
    <SafeAreaProvider>
      <AlertProvider>
        <AuthProvider>
          <StatusBar style="dark" backgroundColor={COLORS.bgDarker} />
          <AppNavigator />
        </AuthProvider>
      </AlertProvider>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.bgDark,
  },
  loadingText: {
    color: COLORS.textSecondary,
    marginTop: 12,
    fontSize: 13,
  },
  tabIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabIconActive: {
    backgroundColor: COLORS.primary + '12',
  },
  tabIconText: {
    fontSize: 20,
  },
});
